// A native <dialog> has its role without the attribute.
const SELF_HANDLED_TARGETS =
   'input, textarea, select, [contenteditable="true"], [role="dialog"], dialog';

// Match the state, not just the slot: SheetContent stays mounted while closed
// for its slide-out and the dialogs through their exit animation, so the slot
// alone reports a modal on every page that renders a closed one, which is
// every page with a step rail.
const OPEN_MODAL = ['dialog-content', 'alert-dialog-content', 'sheet-content', 'drawer-content']
   .map(slot => `[data-slot="${slot}"][data-state="open"]`)
   .join(', ');

/** The user is typing, or focus sits in a panel that handles its own keys. */
export function isSelfHandledTarget(event: Event): boolean {
   const target = event.target;
   return target instanceof Element && target.closest(SELF_HANDLED_TARGETS) !== null;
}

/**
 * body.modal-displayed covers the legacy modal while it loads (Shared/modal.js);
 * .modalBox.modalOpen catches the noOverlay case, which never sets that class.
 */
export function isLegacyModalOpen(): boolean {
   return (
      document.body.classList.contains('modal-displayed') ||
      document.querySelector('.modalBox.modalOpen') !== null
   );
}

/**
 * A modal is open over the editor, so the editor is not what the keys are
 * for. Answered from the document rather than from the event's target: the
 * media manager's markup dialog is a fabric.js canvas in a MooTools modal
 * with no role="dialog" and nothing focused, so a target-based check missed
 * it and arrows meant to nudge a marker walked tabs and steps underneath.
 */
export function isModalOpen(): boolean {
   if (isLegacyModalOpen()) {
      return true;
   }
   // The mobile step rail is a sheet the desktop layout hides with md:hidden.
   // Left open at phone width and then widened, it would otherwise keep every
   // shortcut off with nothing on screen to close.
   return [...document.querySelectorAll(OPEN_MODAL)].some(
      modal => getComputedStyle(modal).display !== 'none'
   );
}

// Counted, not read from the DOM: the media manager and the image dialogs are
// code split, so nothing is there to find during their load (#64245).
let pendingModals = 0;

/**
 * Count a modal as pending from now until the returned release runs. For a
 * dialog whose promise outlives the dialog itself — the crop and markers
 * dialogs go on resolving through a background image job that runs long after
 * the modal leaves the screen — release at dismissal rather than at settle.
 * Releasing twice is a no-op, so a caller can release when the dialog pops and
 * again on the way out without the count going negative.
 */
export function beginModalPending(): () => void {
   pendingModals += 1;
   let released = false;
   return () => {
      if (released) {
         return;
      }
      released = true;
      pendingModals -= 1;
   };
}

/** Run `open` (load, mount, dismissal) with isModalPending() true throughout. */
export async function whileModalPending<T>(open: () => Promise<T>): Promise<T> {
   const release = beginModalPending();
   try {
      return await open();
   } finally {
      release();
   }
}

/**
 * Not folded into isModalOpen(): a dialog whose promise never settles would
 * otherwise switch off every shortcut on the page.
 */
export function isModalPending(): boolean {
   return pendingModals > 0;
}

/** The keystroke belongs to something other than the editor's shortcuts. */
export function isShortcutClaimed(event: Event): boolean {
   return isSelfHandledTarget(event) || isModalOpen();
}
